<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">
				{{ $t("quotes") }}
				<core-pagination-info
					:itemsPerPage="itemsPerPage"
					:numberOfItems="listing.numberOfItems"
					:currentPage="computedPage"
				/>
			</template>
			<template slot="actions">
				<j-button
					v-can="'writeProducts'"
					:to="{ name: 'QuoteCreate' }"
					action="createQuote"
				>
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("create_quote") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Search -->
		<!-- <div class="flex wrap xs-12 xs-left mb2">
      <div class="flex xs-12">
        <j-base-input-search
          :placeholder="$t('search_name')"
          :value="searchTerm"
          @input="onSearch($event)"  />
        <j-button
          class="ml2"
          type="tertiary"
          v-if="searchTerm"
          @click="cancelSearch">
          {{$t('cancel')}}
        </j-button>
      </div>
            </div>-->

		<!-- Data Container -->
		<j-card class="bg5">
			<j-data-table
				:header="header"
				:list="computedList"
				:sortable="false"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in header" :key="i">
						<template>
							<!-- Name -->
							<template v-if="column.field === 'id'">
								<router-link
									:to="{
										name: `QuoteDetail`,
										params: { id: item.id }
									}"
									>{{ item.id }}</router-link
								>
							</template>

							<!-- status -->
							<template v-else-if="column.field === 'status'">
								<!-- Red for REJECTED and yellow for QUOTING -->
								<j-tag
									:label="item.status"
									:variant="decideStatusClass(item.originalStatus)"
								/>
							</template>

							<!-- Others -->
							<template v-else>{{ item[column.field] }}</template>
						</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Pagination -->
		<div class="flex xs-12 p4 xs-center mb15">
			<j-pagination
				:amount="listing.numberOfItems ? listing.numberOfItems : 0"
				:perPage="itemsPerPage"
				:activePage="computedPage"
				@click="onPageChange($event)"
			/>
		</div>
	</core-layout-content>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JTag from "@/components/JTag/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import dayjs from "dayjs"
import api from "../api/"
import get from "lodash.get"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		JTag,
		JPagination,
		CorePaginationInfo,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			listing: {
				numberOfItems: 0,
				list: []
			},

			// Search term
			searchTerm: null,

			// Pagination
			itemsPerPage: 30,

			// Header of table
			header: [
				{
					field: "id",
					text: this.$t("quote_number")
				},
				{
					field: "clientName",
					text: this.$t("client")
				},
				{
					field: "insuranceType",
					label: this.$t("type")
				},
				{
					field: "insurerName",
					label: this.$t("insurer")
				},
				{
					field: "productName",
					text: this.$t("product")
				},
				{
					field: "premium",
					text: this.$t("premium")
				},
				{
					field: "discountCodes",
					label: this.$t("discount_codes")
				},
				{
					field: "status",
					text: this.$t("status")
				},
				{
					text: this.$t("created"),
					field: "createdAt"
				}
			]
		}
	},

	computed: {
		/**
		 * Compute fetch data for data table
		 * This is what we are gonna display
		 */
		computedList() {
			if (!this.listing.list) return []

			return this.listing.list.map(item => ({
				id: item.id,
				clientName: get(item, "holder.name"),
				originalStatus: item.status,
				status: item.status ? this.$t(item.status) : "",
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null,

				insurerName: this.isCaseFlow(item)
					? item?.product?.insurer?.name
					: get(item, "acceptedOffer.product.insurer.name"),
				productName: this.isCaseFlow(item)
					? item?.product?.name
					: get(item, "acceptedOffer.product.name"),
				premium: this.isCaseFlow(item)
					? item?.premium?.formattedPrice
					: get(item, "acceptedOffer.premium.formattedPrice"),
				insuranceType: get(item, "acceptedOffer.product.productId.type")
					? this.$t(
							`insurance_types.${get(
								item,
								"acceptedOffer.product.productId.type"
							)}`
					  )
					: this.$t(`insurance_types.${get(item, "product.productId.type")}`),
				discountCodes: get(item, "acceptedOffer.premium.appliedDiscounts")
					? item.acceptedOffer.premium.appliedDiscounts
							.map(item => item.code)
							.join(",")
					: ""
			}))
		},

		computedPage() {
			return this.$store.state.quotes.listing.page
		}
	},

	methods: {
		isCaseFlow(policy) {
			if (policy?.acceptedOffer) return false
			return true
		},
		onPageChange(pageIndex) {
			this.$store.commit("quotes/SET_PAGE", pageIndex)
			this.readAll()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch(value) {
			// const payload = value === null || value === "" ? null : value
			this.searchTerm = value
			this.$store.commit("quotes/SET_PAGE", 1)
			this.readAll()
		},

		cancelSearch() {
			this.searchTerm = null
			this.$store.commit("quotes/SET_PAGE", 1)
			this.onSearch()
		},

		async readAll() {
			// Sorting - sort by createdAt
			let sort = { fieldName: "createdAt", type: "desc" }

			// Pagination
			const skip = (this.computedPage - 1) * this.itemsPerPage
			const limit = this.itemsPerPage

			// Search
			// NOTE: Quotes are actually policies with a special status
			let where = {
				status_in: ["QUOTING", "REJECTED", null]
			}

			// this.searchTerm
			//   ? {
			//       productId: { plan_contains: `/${this.searchTerm}/gi` }
			//     }
			//   : null;

			this.isLoading = true
			const res = await api.quotes({
				sort,
				skip,
				limit,
				where
			})
			this.isLoading = false

			// Check errors
			if (res.errors) this.$store.dispatch("showError", res.errors)

			this.listing.list = get(res, "data.policies.list")
			this.listing.numberOfItems = get(res, "data.policies.totalCount")
		},

		decideStatusClass(status) {
			if (status === "QUOTING" || status === "UNDEFINED") return "warning"
			if (status === "REJECTED") return "error"
		}
	},

	beforeRouteLeave(to, from, next) {
		// If in same module , do nothing, do not reset page
		if (to.meta.featureId === from.meta.featureId) {
			next()
		} else {
			// if not in same module, reset page to 1
			this.$store.commit("quotes/SET_PAGE", 1)
			next()
		}
	},

	mounted() {
		this.readAll()
	}
}
</script>
